import React, { useState } from 'react';
import { Gift } from '../../types';
import { ArrowLeft, Gift as GiftIcon, Calendar, Phone, Lock } from 'lucide-react';
import { QRCodeDisplay } from '../QRCodeDisplay';
interface GiftRedeemScreenProps {
  gift: Gift;
  onBack: () => void;
}
export function GiftRedeemScreen({
  gift,
  onBack
}: GiftRedeemScreenProps) {
  const [isCodeRevealed, setIsCodeRevealed] = useState(false);
  const handleRedeem = () => {
    setIsCodeRevealed(true);
  };
  const expiryDate = new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000);
  return <div className="pb-24 pt-6 px-5 min-h-screen bg-gradient-to-br from-purple-500 to-purple-700" data-id="element-896">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6" data-id="element-897">
        <button onClick={onBack} className="w-12 h-12 bg-[#E6FF00] rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-898">
          <ArrowLeft size={24} className="text-gray-900" strokeWidth={2.5} data-id="element-899" />
        </button>
        <h1 className="text-2xl font-bold text-white" data-id="element-900">Redeem Gift</h1>
      </div>

      {/* Main Content Card */}
      <div className="bg-[#F0EEFF] rounded-3xl p-6 mb-6" data-id="element-901">
        {/* Gift Image */}
        <div className="w-full h-48 rounded-2xl overflow-hidden mb-4" data-id="element-902">
          <img src={gift.itemImage} alt={gift.itemName} className="w-full h-full object-cover" data-id="element-903" />
        </div>

        {/* Gift Details */}
        <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-904">
          {gift.itemName}
        </h2>
        <p className="text-gray-600 text-base mb-4" data-id="element-905">{gift.storeName}</p>

        {/* Gift From */}
        <div className="flex items-center text-sm text-gray-600 mb-6" data-id="element-906">
          <GiftIcon size={16} className="mr-2 text-[#F7C300]" data-id="element-907" />
          <span data-id="element-908">Gift from {gift.sender}</span>
        </div>

        {/* Redemption Code Section */}
        <div className="bg-[#FFFBE6] rounded-2xl p-5 mb-6" data-id="element-909">
          <p className="text-sm font-bold text-gray-800 mb-3" data-id="element-910">
            Redemption Code:
          </p>

          {isCodeRevealed ? <div className="bg-white rounded-xl p-6 text-center animate-in fade-in duration-300" data-id="element-911">
              {/* QR Code */}
              <div className="flex justify-center mb-4" data-id="element-912">
                <QRCodeDisplay value={gift.code} data-id="element-913" />
              </div>

              {/* Code Text */}
              <p className="font-mono text-2xl font-bold tracking-wider text-gray-900 mb-2" data-id="element-914">
                {gift.code}
              </p>
              <p className="text-xs text-gray-500" data-id="element-915">
                Scan QR code or show code to staff
              </p>
            </div> : <div className="bg-gray-100 rounded-xl p-4 text-center" data-id="element-916">
              <div className="flex items-center justify-center text-gray-500" data-id="element-917">
                <Lock size={20} className="mr-2" data-id="element-918" />
                <p className="font-medium text-base" data-id="element-919">
                  Click Redeem to reveal code
                </p>
              </div>
            </div>}

          <p className="text-xs text-gray-500 mt-3 text-center" data-id="element-920">
            {isCodeRevealed ? 'Show this code to the staff at checkout' : "Code will be revealed when you're ready to redeem"}
          </p>
        </div>

        {/* Additional Info */}
        <div className="space-y-3 mb-6" data-id="element-921">
          <div className="flex items-center text-sm text-gray-600" data-id="element-922">
            <Calendar size={16} className="mr-2 flex-shrink-0" data-id="element-923" />
            <span data-id="element-924">
              Valid until:{' '}
              {expiryDate.toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </span>
          </div>
          <div className="flex items-center text-sm text-gray-600" data-id="element-925">
            <Phone size={16} className="mr-2 flex-shrink-0" data-id="element-926" />
            <span data-id="element-927">Store phone: (555) 123-4567</span>
          </div>
        </div>

        {/* Redeem Button */}
        <button onClick={handleRedeem} disabled={isCodeRevealed} className={`w-full py-4 rounded-2xl font-bold text-lg transition-all active:scale-[0.98] ${isCodeRevealed ? 'bg-green-500 text-white cursor-default' : 'bg-[#FFFC00] text-gray-900 shadow-lg'}`} data-id="element-928">
          {isCodeRevealed ? '✓ Redemption Confirmed' : 'Redeem Now'}
        </button>
      </div>

      {/* How to Redeem Instructions */}
      <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6" data-id="element-929">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-930">How to redeem:</h3>
        <ol className="space-y-3 text-white text-sm" data-id="element-931">
          <li className="flex gap-3" data-id="element-932">
            <span className="font-bold flex-shrink-0" data-id="element-933">1.</span>
            <span data-id="element-934">Visit {gift.storeName}</span>
          </li>
          <li className="flex gap-3" data-id="element-935">
            <span className="font-bold flex-shrink-0" data-id="element-936">2.</span>
            <span data-id="element-937">Click "Redeem Now" to reveal your code</span>
          </li>
          <li className="flex gap-3" data-id="element-938">
            <span className="font-bold flex-shrink-0" data-id="element-939">3.</span>
            <span data-id="element-940">Show the QR code or redemption code to staff</span>
          </li>
          <li className="flex gap-3" data-id="element-941">
            <span className="font-bold flex-shrink-0" data-id="element-942">4.</span>
            <span data-id="element-943">Enjoy your gift!</span>
          </li>
        </ol>
      </div>
    </div>;
}